import React from 'react';
import { Box, Container, Header, ProgressBar, RadioGroup, SpaceBetween } from '@amzn/awsui-components-core';
import { IS_NON_PROD } from 'src/constants/EnvironmentConstants';
import { OrgChartEmployee } from '../../../types/orgChart.types';
import { Employee } from '../../../../ea-huddle-v2/interfaces/interface';
import { OrgChartTree } from '../../org-chart/OrgChartTree';
import { ParticipantSelector } from '../../../../ea-huddle-v2/components/attendees/ParticipantSelector';

export interface Step2FolderSetupAndSelectProps {
  viewMode: 'direct' | 'all';
  onViewModeChange: (mode: 'direct' | 'all') => void;
  currentLeaderData: OrgChartEmployee | null;
  onLeaderChange: (employee: Employee) => void;
  // Org chart data
  employees: Map<string, OrgChartEmployee>;
  expandedNodes: Set<string>;
  selectedEmployees: Set<string>;
  onExpand: (login: string) => void;
  onCollapse: (login: string) => void;
  onToggleSelection: (login: string) => void;
  // Org-chart loading
  isLoadingOrg: boolean;
  loadingProgress: number;
  totalEmployees: number;
}

/**
 * Step 2: choose Direct vs All Reports, optionally switch leader (non-prod only), and pick which
 * employees get a card — all in a single "Select People" container. WorkDocs folders are already
 * provisioned in Step 1 (auto-run on connect, always the full 'all' tree), so this step no longer
 * provisions; the Direct/All choice here only controls which people are shown and selected.
 */
export const Step2FolderSetupAndSelect: React.FC<Step2FolderSetupAndSelectProps> = ({
  viewMode,
  onViewModeChange,
  currentLeaderData,
  onLeaderChange,
  employees,
  expandedNodes,
  selectedEmployees,
  onExpand,
  onCollapse,
  onToggleSelection,
  isLoadingOrg,
  loadingProgress,
  totalEmployees
}) => (
  <Container
    header={
      <Header variant="h2" description="Everyone is included by default. Uncheck anyone you want to leave out.">
        Choose Who Gets a Card
      </Header>
    }
  >
    <SpaceBetween size="m">
      <RadioGroup
        onChange={({ detail }) => onViewModeChange(detail.value as 'direct' | 'all')}
        value={viewMode}
        items={[
          { value: 'direct', label: 'Direct Reports', description: 'Just the people who report to you directly.' },
          { value: 'all', label: 'All Reports', description: 'Your entire org, including everyone below your direct reports.' }
        ]}
      />

      {IS_NON_PROD && (
        <ParticipantSelector
          onEmployeeSelect={onLeaderChange}
          placeholder="Search for New Leader..."
          existingEmails={currentLeaderData?.email ? new Set([currentLeaderData.email.toLowerCase()]) : new Set()}
          buttonText="Select Leader"
        />
      )}

      {isLoadingOrg ? (
        <SpaceBetween size="s">
          <Box>Loading employee information...</Box>
          <ProgressBar
            value={totalEmployees > 0 ? (loadingProgress / totalEmployees) * 100 : 0}
            additionalInfo={`${loadingProgress} / ${totalEmployees} employees loaded`}
            label="Loading Progress"
          />
        </SpaceBetween>
      ) : currentLeaderData ? (
        <OrgChartTree
          leader={currentLeaderData}
          employees={employees}
          expandedNodes={expandedNodes}
          selectedEmployees={selectedEmployees}
          onExpand={onExpand}
          onCollapse={onCollapse}
          onToggleSelection={onToggleSelection}
        />
      ) : (
        <Box color="text-body-secondary">No employee data loaded yet.</Box>
      )}
    </SpaceBetween>
  </Container>
);
